import { format } from 'date-fns'

import { formatCurrency } from '../utils/currencyUtils'
import { transformKeyPath } from '../utils/activityLogDisplay'

export type ActivityFieldConfig = {
  label: string
  hidden?: boolean
  priority?: number
  formatValue?: (value: unknown) => string | null
  updatePhrase?: (args: {
    oldValue?: unknown
    newValue?: unknown
  }) => string | null
}

export const DEFAULT_FIELD_CONFIG: ActivityFieldConfig = {
  label: '',
  hidden: true,
}

const HIDDEN_KEY_PATHS = new Set([
  'posCode',
  'customerTags',
  'modified',
  'modifiedBy',
  'created',
  'createdDiary',
  'posTickets',
  'receipts',
])

const HIDDEN_KEY_PATH_PREFIXES = ['pricing.', 'metadata.']

function titleCase(value: string): string {
  return value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function formatTableValue(value: unknown): string | null {
  if (!Array.isArray(value) || value.length === 0) return null
  const first = value[0] as { name?: string }
  if (first?.name) return first.name
  return `${value.length} table${value.length === 1 ? '' : 's'}`
}

function formatDateValue(value: unknown): string | null {
  if (value == null || value === '') return null
  const date = new Date(value as string | number | Date)
  if (Number.isNaN(date.getTime())) return String(value)
  return format(date, 'MMM d, yyyy')
}

function formatVipValue(value: unknown): string | null {
  if (value === true) return 'VIP'
  if (value === false) return 'Standard'
  return null
}

function formatBooleanValue(value: unknown): string | null {
  if (value === true) return 'Yes'
  if (value === false) return 'No'
  return null
}

export function formatCancellationRefundLabel(value: unknown): string | null {
  if (value === true) return 'Refund: Yes'
  if (value === false) return 'Refund: No'
  return null
}

function formatNotesValue(value: unknown): string | null {
  if (value == null || value === '') return null
  const text = String(value)
  if (text.length <= 80) return text
  return `${text.slice(0, 77)}…`
}

function formatConfirmationNotificationValue(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null

  const payload = value as {
    action?: unknown
    channel?: unknown
    recipient?: unknown
  }

  const action =
    typeof payload.action === 'string' && payload.action.trim()
      ? payload.action.trim().toLowerCase()
      : null
  const channel =
    typeof payload.channel === 'string' && payload.channel.trim()
      ? payload.channel.trim().toLowerCase()
      : null
  const recipient =
    typeof payload.recipient === 'string' && payload.recipient.trim()
      ? payload.recipient.trim()
      : null

  if (!action && !channel && !recipient) return null

  const parts: string[] = []
  if (action) parts.push(action)
  if (channel) parts.push(channel)

  if (recipient) {
    return `${parts.join(' ')} to ${recipient}`.trim()
  }

  return parts.join(' ').trim() || null
}

export const ACTIVITY_FIELD_MAP: Record<string, ActivityFieldConfig> = {
  name: {
    label: 'Guest name',
    priority: 1,
    formatValue: (value) => (value != null ? String(value) : null),
    updatePhrase: ({ newValue }) =>
      newValue != null ? `updated guest name to ${String(newValue)}` : null,
  },
  status: {
    label: 'Status',
    priority: 3,
    formatValue: (value) => (value != null ? titleCase(String(value)) : null),
    updatePhrase: ({ newValue }) =>
      newValue != null
        ? `changed status to ${titleCase(String(newValue))}`
        : null,
  },
  vip: {
    label: 'VIP status',
    priority: 2,
    formatValue: formatVipValue,
    updatePhrase: ({ newValue }) => {
      const formatted = formatVipValue(newValue)
      return formatted ? `changed VIP status to ${formatted}` : null
    },
  },
  tables: {
    label: 'Table',
    priority: 4,
    formatValue: formatTableValue,
    updatePhrase: ({ newValue }) => {
      const tableName = formatTableValue(newValue)
      return tableName ? `assigned table ${tableName}` : null
    },
  },
  minimumSpend: {
    label: 'Minimum spend',
    priority: 6,
    formatValue: (value) =>
      typeof value === 'number'
        ? formatCurrency({ value })
        : value != null
          ? String(value)
          : null,
    updatePhrase: ({ newValue }) => {
      if (typeof newValue !== 'number') return null
      return `changed minimum spend to ${formatCurrency({ value: newValue })}`
    },
  },
  guestsExpected: {
    label: 'Expected guests',
    priority: 7,
    formatValue: (value) => (value != null ? String(value) : null),
    updatePhrase: ({ newValue }) =>
      newValue != null ? `set expected guests to ${String(newValue)}` : null,
  },
  guestsCheckedIn: {
    label: 'Checked in',
    priority: 8,
    formatValue: (value) => (value != null ? String(value) : null),
    updatePhrase: ({ newValue }) =>
      newValue != null ? `set checked-in guests to ${String(newValue)}` : null,
  },
  eventName: {
    label: 'Event',
    priority: 5,
    formatValue: (value) => (value != null ? String(value) : null),
    updatePhrase: ({ newValue }) =>
      newValue != null ? `changed event to ${String(newValue)}` : null,
  },
  serverName: {
    label: 'Server',
    priority: 9,
    formatValue: (value) => (value != null ? String(value) : null),
    updatePhrase: ({ newValue }) =>
      newValue != null ? `assigned server ${String(newValue)}` : null,
  },
  notes: {
    label: 'Notes',
    priority: 14,
    formatValue: formatNotesValue,
    updatePhrase: ({ newValue }) => (newValue != null ? 'updated notes' : null),
  },
  date: {
    label: 'Date',
    priority: 12,
    formatValue: formatDateValue,
    updatePhrase: ({ newValue }) => {
      const formatted = formatDateValue(newValue)
      return formatted ? `changed date to ${formatted}` : null
    },
  },
  lead: {
    label: 'Lead',
    priority: 13,
    formatValue: formatBooleanValue,
    updatePhrase: ({ newValue }) => {
      const formatted = formatBooleanValue(newValue)
      return formatted != null ? `set lead to ${formatted}` : null
    },
  },
  assignedTo: {
    label: 'Assigned to',
    priority: 11,
    formatValue: (value) => (value != null ? String(value) : null),
    updatePhrase: ({ newValue }) =>
      newValue != null ? `assigned to ${String(newValue)}` : null,
  },
  attributionName: {
    label: 'Attribution',
    priority: 10,
    formatValue: (value) => (value != null ? String(value) : null),
    updatePhrase: ({ newValue }) =>
      newValue != null ? `changed attribution to ${String(newValue)}` : null,
  },
  cancelledReasonType: {
    label: 'Cancel reason',
    priority: 15,
    formatValue: (value) => (value != null ? titleCase(String(value)) : null),
    updatePhrase: ({ newValue }) =>
      newValue != null
        ? `set cancel reason to ${titleCase(String(newValue))}`
        : null,
  },
  cancellationRefund: {
    label: 'Refund',
    priority: 16,
    formatValue: formatCancellationRefundLabel,
    updatePhrase: ({ newValue }) => formatCancellationRefundLabel(newValue),
  },
  paymentRefund: {
    label: 'Refund',
    priority: 20,
    formatValue: (value) => {
      if (value == null || typeof value !== 'object') return null
      const refund = value as { amount?: number }
      if (refund.amount == null) return null
      return formatCurrency({ value: refund.amount })
    },
    updatePhrase: ({ newValue }) => {
      if (newValue == null || typeof newValue !== 'object') return null
      const refund = newValue as {
        amount?: number
        label?: string
        processingFeeWithheld?: number
      }
      if (refund.amount == null) return null
      const amountPhrase = `refunded ${formatCurrency({ value: refund.amount })}`
      const processingFeePhrase =
        typeof refund.processingFeeWithheld === 'number' &&
        refund.processingFeeWithheld > 0
          ? '. Processing fees are not refunded.'
          : ''
      const label = refund.label?.trim()
      if (label) return `${label} — ${amountPhrase}${processingFeePhrase}`
      return `${amountPhrase}${processingFeePhrase}`
    },
  },
  confirmationNotification: {
    label: 'Confirmation notification',
    priority: 17,
    hidden: false,
    formatValue: formatConfirmationNotificationValue,
    updatePhrase: ({ newValue }) => {
      const formatted = formatConfirmationNotificationValue(newValue)
      return formatted ? `confirmation notification ${formatted}` : null
    },
  },
}

export function isHiddenActivityKeyPath(keyPath: string): boolean {
  if (HIDDEN_KEY_PATHS.has(keyPath)) return true
  return HIDDEN_KEY_PATH_PREFIXES.some((prefix) => keyPath.startsWith(prefix))
}

export function getActivityFieldConfig(keyPath: string): ActivityFieldConfig {
  if (isHiddenActivityKeyPath(keyPath)) {
    return DEFAULT_FIELD_CONFIG
  }

  const config = ACTIVITY_FIELD_MAP[keyPath]
  if (config) return config

  return DEFAULT_FIELD_CONFIG
}

export function getActivityFieldLabel(keyPath: string): string {
  const config = getActivityFieldConfig(keyPath)
  if (!config.hidden && config.label) {
    return config.label
  }
  return transformKeyPath(keyPath)
}
